<!-- FIELD VARIANT -->
<ng-container *ngIf="variant === 'field'">
  <div
    class="anc-field anc-date-picker__field"
    [class.anc-field--floating]="floating()"
    [class.anc-field--focused]="focused()"
    [class.anc-date-picker__field--labeled]="!!label"
  >
    <div
      class="anc-date-picker__control"
      [class.anc-date-picker__control--single]="singleDatePicker"
    >
      @if (embeddedControl(); as ctrl) {
        <input
          type="text"
          ngxDaterangepickerMd
          [formControl]="ctrl"
          [locale]="effectiveLocale"
          [showDropdowns]="showDropdowns"
          [showCancel]="showCancel"
          [singleDatePicker]="singleDatePicker"
          [linkedCalendars]="effectiveLinkedCalendars"
          [minDate]="effectiveMinDate"
          [maxDate]="effectiveMaxDate"
          [isInvalidDate]="effectiveIsInvalidDate"
          [disabled]="disabled"
          readonly
          ancFieldValidate
          [ancFieldValidateSubmitted]="validateSubmitted()"
          (datesUpdated)="onDatesUpdated($event)"
        />
      } @else if (!controlName()) {
        <ng-content select="input[ngxDaterangepickerMd]"></ng-content>
      }
      <button
        type="button"
        class="anc-date-picker__icon-btn"
        tabindex="-1"
        [disabled]="disabled"
        (mousedown)="$event.preventDefault()"
        (click)="onIconClick($event)"
        [attr.aria-label]="calendarTriggerAriaLabel()"
      >
        <i class="icon icon-calendar" aria-hidden="true"></i>
      </button>
    </div>
    <label *ngIf="label" class="anc-field__label" translate>
      {{ label }}<sup class="super-star" *ngIf="required">*</sup>
    </label>
    @if (embeddedControl(); as ctrl) {
      @if (showEmbeddedRequiredError(ctrl)) {
        <div ancError>
          <div class="has-error" translate>{{ requiredErrorMessage() }}</div>
        </div>
      }
    }
    <ng-content select="[ancError]"></ng-content>
  </div>
</ng-container>

<!-- FILTER VARIANT — button trigger -->
<ng-container *ngIf="variant === 'filter' && filterDisplay === 'button'">
  <div
    class="anc-date-picker__filter anc-date-picker__filter--button"
    [class.anc-date-picker__filter--active]="active"
  >
    <div class="anc-date-picker__filter-trigger">
      <button
        type="button"
        libAncButton
        size="default"
        [variant]="filterButtonVariant"
        [disabled]="disabled"
        (click)="onFilterButtonClick($event)"
      >
        <span translate>{{ filterLabel }}</span>
        <i class="icon icon-calendar" aria-hidden="true"></i>
      </button>
      <input
        type="text"
        class="anc-date-picker__input anc-date-picker__input--visually-hidden"
        [attr.aria-label]="label || filterLabel"
        ngxDaterangepickerMd
        [(ngModel)]="pickerModel"
        [locale]="effectiveLocale"
        [singleDatePicker]="singleDatePicker"
        [showDropdowns]="showDropdowns"
        [showCancel]="showCancel"
        [linkedCalendars]="effectiveLinkedCalendars"
        [drops]="drops"
        [minDate]="effectiveMinDate"
        [maxDate]="effectiveMaxDate"
        [isInvalidDate]="effectiveIsInvalidDate"
        [disabled]="disabled"
        readonly
        (datesUpdated)="onDatesUpdated($event)"
      />
    </div>
  </div>
</ng-container>

<!-- DS MONTH / YEAR PICKER OVERLAY — replaces ngx native month/year selects -->
<div
  *ngIf="pickerView() !== 'date'"
  #dpOverlay
  class="anc-dp-overlay"
  [ngStyle]="overlayStyle()"
  (pointerdown)="onOverlayInternalEvent($event)"
  (mousedown)="onOverlayInternalEvent($event)"
  (click)="onOverlayInternalEvent($event)"
>
  <div class="anc-dp-overlay__panel">
    <div class="anc-dp-overlay__header">
      <button
        type="button"
        class="anc-dp-overlay__nav"
        (click)="onOverlayPrev()"
        aria-label="Previous"
      >
        <i class="icon icon-chevron-left" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="anc-dp-overlay__title"
        (click)="onTitleClick()"
      >
        <span>{{ overlayTitle() }}</span>
        <i
          class="icon icon-chevron-down anc-dp-overlay__caret"
          aria-hidden="true"
        ></i>
      </button>
      <button
        type="button"
        class="anc-dp-overlay__nav"
        (click)="onOverlayNext()"
        aria-label="Next"
      >
        <i class="icon icon-chevron-right" aria-hidden="true"></i>
      </button>
    </div>

    <div *ngIf="pickerView() === 'month'" class="anc-dp-overlay__grid">
      <button
        type="button"
        *ngFor="let month of months; let i = index"
        class="anc-dp-chip"
        [class.anc-dp-chip--selected]="isMonthSelected(i)"
        [disabled]="isMonthDisabled(i)"
        (click)="selectMonth(i)"
      >
        {{ month }}
      </button>
    </div>

    <div *ngIf="pickerView() === 'year'" class="anc-dp-overlay__grid">
      <button
        type="button"
        *ngFor="let year of yearList()"
        class="anc-dp-chip"
        [class.anc-dp-chip--selected]="isYearSelected(year)"
        [disabled]="isYearDisabled(year)"
        (click)="selectYear(year)"
      >
        {{ year }}
      </button>
    </div>
  </div>
</div>

<!-- FILTER VARIANT — visible input -->
<ng-container *ngIf="variant === 'filter' && filterDisplay === 'input'">
  <div
    class="anc-date-picker__filter anc-date-picker__filter--input"
    [class.anc-date-picker__filter--active]="active"
    [class.anc-date-picker__filter--focused]="focused()"
  >
    <input
      type="text"
      class="anc-date-picker__input"
      [attr.aria-label]="label || placeholder || filterLabel"
      ngxDaterangepickerMd
      [(ngModel)]="pickerModel"
      [placeholder]="placeholder"
      [locale]="effectiveLocale"
      [singleDatePicker]="singleDatePicker"
      [showDropdowns]="showDropdowns"
      [showCancel]="showCancel"
      [linkedCalendars]="effectiveLinkedCalendars"
      [drops]="drops"
      [minDate]="effectiveMinDate"
      [maxDate]="effectiveMaxDate"
      [isInvalidDate]="effectiveIsInvalidDate"
      [disabled]="disabled"
      readonly
      (datesUpdated)="onDatesUpdated($event)"
    />
    <button
      type="button"
      class="anc-date-picker__icon-btn"
      [disabled]="disabled"
      (mousedown)="$event.preventDefault()"
      (click)="onIconClick($event)"
      [attr.aria-label]="filterLabel"
    >
      <i class="icon icon-calendar" aria-hidden="true"></i>
    </button>
  </div>
</ng-container>
